import React, { forwardRef } from 'react';
import { color, space, font, radius } from './tokens';

// ---------------------------------------------------------------------------
// Input — Wave 4.1.A
//
// Text input. forwardRef + aria-invalid + aria-describedby for FormField
// integration. Visual focus ring uses the brand colour so it inherits the
// merchant branding via CSS var.
// ---------------------------------------------------------------------------

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Pass true when the field has a validation error — turns the border red. */
  invalid?: boolean;
  /** Visual size variant; default = md. */
  inputSize?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: { padding: `${space[1]}px ${space[2]}px`, fontSize: font.size.sm,  minHeight: 32 },
  md: { padding: `${space[2]}px ${space[3]}px`, fontSize: font.size.base, minHeight: 40 },
  lg: { padding: `${space[3]}px ${space[4]}px`, fontSize: font.size.lg,  minHeight: 48 },
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid = false, inputSize = 'md', style, disabled, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      style={{
        display:      'block',
        width:        '100%',
        background:   disabled ? color.bg.subtle : color.bg.card,
        color:        color.text.primary,
        border:       `1px solid ${invalid ? color.status.error.border : color.border.strong}`,
        borderRadius: radius.md,
        fontFamily:   font.family.sans,
        outline:      'none',
        transition:   'border-color 120ms, box-shadow 120ms',
        ...sizeStyles[inputSize],
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = invalid
          ? color.status.error.text
          : color.border.focus;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${invalid ? 'rgba(220, 38, 38, 0.2)' : 'rgba(37, 99, 235, 0.2)'}`;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = invalid
          ? color.status.error.border
          : color.border.strong;
        e.currentTarget.style.boxShadow = 'none';
      }}
      {...rest}
    />
  );
});

export default Input;
